import { useState, useEffect, useCallback } from "react";
import { ListGroup, Container, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Notyf } from "notyf";

const notyf = new Notyf();

export default function Orders() {
  const [orderHistory, setOrderHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setIsLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/orders/my-orders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      /*
       * No orders should NOT be treated as a page-breaking error.
       * Some APIs return 404 when there are no orders.
       */
      if (res.status === 404) {
        setOrderHistory([]);
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      /*
       * If the backend returns:
       * { orders: [] }
       *
       * simply display the empty state.
       */
      if (!data.orders || data.orders.length === 0) {
        setOrderHistory([]);
        return;
      }

      /*
       * Fetch product names for each order item.
       */
      const ordersWithProductNames = await Promise.all(
        data.orders.map(async (order) => {
          const productsWithNames = await Promise.all(
            order.productsOrdered.map(async (item) => {
              try {
                const productRes = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL}/products/${item.productId}`
                );

                if (!productRes.ok) {
                  return {
                    ...item,
                    productName: "Product unavailable",
                  };
                }

                const productData = await productRes.json();

                return {
                  ...item,
                  productName: productData.name || "Product unavailable",
                };
              } catch (error) {
                console.error("Error fetching product:", error);

                return {
                  ...item,
                  productName: "Product unavailable",
                };
              }
            })
          );

          return {
            ...order,
            productsOrdered: productsWithNames,
          };
        })
      );

      setOrderHistory(ordersWithProductNames);
    } catch (error) {
      console.error("Error fetching orders:", error);

      /*
       * Only show an actual error if something unexpected happened.
       */
      notyf.error("Failed to load your order history.");
      setOrderHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  if (isLoading) {
    return (
      <Container className="orders-page mt-5">
        <div className="text-center py-5">
          <p>Loading your order history...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="orders-page mt-5 mb-5">
      <h2 className="text-center mb-4">Order History</h2>

      {orderHistory.length === 0 ? (
        /*
         * EMPTY ORDER STATE
         */
        <div className="orders-empty-state text-center">
          <div className="orders-empty-icon">
            <i className="bi bi-bag-heart"></i>
          </div>

          <h3>No orders yet</h3>

          <p>
            You haven't placed any orders yet. Browse our desserts and find
            something delicious to enjoy!
          </p>

          <Button
            className="dh-button mt-3"
            onClick={() => navigate("/products")}
          >
            Browse Our Desserts
          </Button>
        </div>
      ) : (
        /*
         * ORDER HISTORY
         */
        <ListGroup>
          {orderHistory.map((order) => (
            <ListGroup.Item key={order._id} className="mb-3 orders-item">
              <h6>Order ID: {order._id}</h6>

              {order.productsOrdered.map((item, index) => (
                <div
                  key={`${item.productId}-${index}`}
                  className="orders-product"
                >
                  <h5>
                    <strong>
                      <span className="orders-itemname">
                        {item.productName}
                      </span>
                    </strong>{" "}
                    {item.quantity}
                  </h5>
                </div>
              ))}

              <div className="mt-2">
                <strong>Total Price:</strong>{" "}
                <span className="orders-totalprice">
                  PhP {order.totalPrice}
                </span>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </Container>
  );
}
